import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink } from 'lucide-react'

import { Button } from '@/components/ui/button'

/**
 * The Adnuntius ad-in-message script. It renders the transparency overlay that
 * a delivered creative gets, so the preview shows what a reader would see.
 */
const AIM_SCRIPT_URL = 'https://assets.adnuntius.com/aim.js'

function withAimScript(html: string): string {
  if (html.includes(AIM_SCRIPT_URL)) return html
  const tag = `<script src="${AIM_SCRIPT_URL}"></script>`
  const headEnd = html.indexOf('</head>')
  if (headEnd > -1) return html.slice(0, headEnd) + tag + html.slice(headEnd)
  const bodyEnd = html.lastIndexOf('</body>')
  if (bodyEnd > -1) return html.slice(0, bodyEnd) + tag + html.slice(bodyEnd)
  return html + tag
}

/** Opens the rendered creative on a page of its own, outside the app. */
export function openPreviewInTab(html: string) {
  const url = URL.createObjectURL(new Blob([withAimScript(html)], { type: 'text/html' }))
  window.open(url, '_blank', 'noopener')
  // The new tab has loaded it by then; the URL only holds the blob in memory
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

interface CreativePreviewFrameProps {
  /** Rendered creative HTML, as the ad server returns it. */
  html: string
  width: number
  height: number
  /**
   * Largest area the frame may occupy; anything bigger is scaled down to fit.
   * The width is measured from the container when not given, so the preview
   * fills whatever column it lands in.
   */
  maxWidth?: number
  maxHeight?: number
  /** FLEXIBLE / FIXED_WIDTH / FIXED_HEIGHT — explains which dimensions were assumed. */
  dimensionType?: string
  /** False when the surrounding card offers "open on blank page" itself. */
  showOpenInTab?: boolean
}

/**
 * Shows a rendered creative in a sandboxed iframe, running its scripts so it
 * behaves as it would when delivered. The sandbox keeps it off the app's origin
 * and stops it navigating the page it sits in; the creative is scaled down when
 * it is larger than the space available.
 */
export function CreativePreviewFrame({
  html,
  width,
  height,
  maxWidth,
  maxHeight,
  dimensionType,
  showOpenInTab = true,
}: CreativePreviewFrameProps) {
  const { t } = useTranslation()
  const content = useMemo(() => withAimScript(html), [html])
  const viewport = useRef<HTMLDivElement>(null)
  const [available, setAvailable] = useState(0)

  // Track the space the frame actually has, so resizing the window rescales it
  useLayoutEffect(() => {
    const el = viewport.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => setAvailable(entry.contentRect.width))
    observer.observe(el)
    setAvailable(el.clientWidth)
    return () => observer.disconnect()
  }, [])

  const scale = useMemo(() => {
    if (!width || !height) return 1
    const widthLimit = Math.min(maxWidth ?? Infinity, available || Infinity)
    const heightLimit = maxHeight ?? Infinity
    if (widthLimit === Infinity && heightLimit === Infinity) return 1
    return Math.min(1, widthLimit / width, heightLimit / height)
  }, [maxWidth, maxHeight, available, width, height])

  const dimensionNote =
    dimensionType === 'FLEXIBLE'
      ? t('creatives.previewDimensionsFlexible', { width, height })
      : dimensionType === 'FIXED_WIDTH'
        ? t('creatives.previewDimensionsFixedWidth', { height })
        : dimensionType === 'FIXED_HEIGHT'
          ? t('creatives.previewDimensionsFixedHeight', { width })
          : null

  return (
    <div className="flex flex-col gap-3">
      {(dimensionNote || showOpenInTab) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-(--ad-text-muted)">{dimensionNote}</span>
          {showOpenInTab && (
            <Button type="button" variant="ghost" size="sm" onClick={() => openPreviewInTab(html)}>
              <ExternalLink className="size-3.5" />
              {t('creatives.previewOpenBlankPage')}
            </Button>
          )}
        </div>
      )}

      {/* Inline-size containment keeps the creative's width from stretching the
          viewport (and whatever grid it sits in), so the width it measures is the
          space on offer and wide creatives get scaled down instead of spilling out. */}
      <div
        ref={viewport}
        className="flex items-center justify-center overflow-auto rounded-md bg-(--ad-neutral-soft) p-4 contain-inline-size"
        style={{ minHeight: Math.min(height * scale + 32, (maxHeight ?? height) + 32) }}
      >
        <div style={{ width: width * scale, height: height * scale }}>
          <iframe
            title={t('creatives.preview')}
            srcDoc={content}
            width={width}
            height={height}
            scrolling="no"
            sandbox="allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox"
            style={{
              display: 'block',
              border: '1px solid var(--ad-border)',
              transform: scale === 1 ? undefined : `scale(${scale})`,
              transformOrigin: '0 0',
            }}
          />
        </div>
      </div>
    </div>
  )
}
